import { useState, useEffect } from 'react'
import { usePublicClient } from 'wagmi'
import { parseAbi, formatEther } from 'viem'
import { Skeleton } from '../components/Skeleton'

const STAKING_ADDRESS = import.meta.env.VITE_STAKING_CONTRACT_ADDRESS
const SEPOLIA_ID = 11155111

const STAKING_ABI = parseAbi([
  'function globalTotalStaked() external view returns (uint256)',
  'function minStakeAmount() external view returns (uint256)',
])

function fmtClt(wei) {
  if (wei == null) return '—'
  const n = Number(formatEther(wei))
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 })
}

function NotDeployedBanner() {
  return (
    <div className="border-4 border-dashed border-on-background/20 p-10 text-center flex flex-col gap-3 items-center">
      <span className="material-symbols-outlined text-[40px] opacity-30">construction</span>
      <p className="font-extrabold uppercase tracking-widest text-[13px]">Staking contract not connected</p>
      <p className="text-[12px] text-on-surface-variant">Set VITE_STAKING_CONTRACT_ADDRESS in your Vercel env and redeploy.</p>
    </div>
  )
}

function ErrorBanner({ message, onRetry }) {
  return (
    <div className="border-4 border-error bg-error-container/20 p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
      <div>
        <p className="font-extrabold text-[13px] uppercase tracking-widest text-on-error-container">Couldn't read staking data</p>
        <p className="text-[12px] text-on-surface-variant mt-1 break-all">{message}</p>
      </div>
      <button onClick={onRetry}
        className="px-5 py-2.5 bg-on-background text-surface font-bold text-[11px] uppercase tracking-widest hover:opacity-90 transition-opacity border-2 border-on-background shrink-0">
        Retry
      </button>
    </div>
  )
}

function VolumeCard({ label, value, icon, sub }) {
  return (
    <div className="border-4 border-on-background bg-surface-container-low p-6 flex flex-col gap-2"
      style={{ boxShadow: '4px 4px 0px 0px var(--neo-border-color)' }}>
      <div className="flex items-center gap-2 text-on-surface-variant">
        <span className="material-symbols-outlined text-[18px]">{icon}</span>
        <p className="font-bold text-[11px] uppercase tracking-widest">{label}</p>
      </div>
      <p className="font-extrabold text-[28px] leading-none">{value}</p>
      {sub && <p className="text-[11px] text-on-surface-variant">{sub}</p>}
    </div>
  )
}

export function StakingManagement() {
  const publicClient = usePublicClient({ chainId: SEPOLIA_ID })

  const [globalTotalStaked, setGlobalTotalStaked] = useState(null)
  const [minStakeAmount, setMinStakeAmount] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchTotals = async () => {
    if (!publicClient || !STAKING_ADDRESS) {
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const [total, min] = await Promise.all([
        publicClient.readContract({
          address: STAKING_ADDRESS,
          abi: STAKING_ABI,
          functionName: 'globalTotalStaked',
        }),
        publicClient.readContract({
          address: STAKING_ADDRESS,
          abi: STAKING_ABI,
          functionName: 'minStakeAmount',
        }),
      ])
      setGlobalTotalStaked(total)
      setMinStakeAmount(min)
    } catch (e) {
      setError(e.shortMessage ?? e.message ?? 'Failed to read staking contract')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchTotals()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicClient])

  if (!STAKING_ADDRESS) return <NotDeployedBanner />

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-extrabold text-[18px] uppercase tracking-widest">Staking Management</h2>
          <p className="text-[12px] text-on-surface-variant mt-1">Read directly from the ChicagoStaking contract on Sepolia.</p>
        </div>
        <button onClick={fetchTotals} disabled={loading}
          className="px-4 py-2 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest hover:bg-primary-container hover:text-on-primary-container transition-colors disabled:opacity-50">
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {error && <ErrorBanner message={error} onRetry={fetchTotals} />}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {loading ? (
          <>
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
          </>
        ) : (
          <>
            <VolumeCard
              label="Total CLT Staked"
              icon="lock"
              value={`${fmtClt(globalTotalStaked)} CLT`}
              sub="Sum of every active (non-withdrawn) stake on the platform"
            />
            <VolumeCard
              label="Minimum Stake Amount"
              icon="tune"
              value={`${fmtClt(minStakeAmount)} CLT`}
              sub="Current floor required to open a new stake"
            />
          </>
        )}
      </div>

      <p className="text-[11px] text-on-surface-variant/60 font-mono">
        Note: ChicagoStaking only accepts CLT — there is no ETH staking on this platform, so no ETH figure is shown here.
      </p>
    </div>
  )
}